import { useEffect, useMemo } from 'react';
import { useParams } from 'quartz-routing/react-router';
import { NavMarker } from 'react-rhumb';
import { PortalsAppLayout, InviteButton, usePortalsAuth, usePortalNavigation, PortalsAuthGuard, PageNotFound } from 'portals-platform-lib';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useGetDealPlanQuery, useGetOwnerCandidatesQuery } from '../../__generated__/chirp/com/hubspot/deal/plans/rpc/DealPlanPortalRpc.hooks';
import { useGetAvatarsBulkQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AvatarsRpc.hooks';
import { useGetCrmObjectContactsQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/DynamicPortalRpc.hooks';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { AvatarsProvider } from '../contexts/AvatarsContext';
import { DealPlanName } from '../components/DealPlanName';
import { DealPlanDescriptionHoverButton } from '../components/styledComponents/DealPlanDescriptionHoverButton';
import { useDealPlanActions } from '../hooks/useDealPlanActions';
import { BuyerLogoSection } from '../components/Header/BuyerLogoSection';
import { DealPlanHeaderMenu } from '../components/Header/DealPlanHeaderMenu';
import { HeaderAvatarGroup } from '../components/Header/HeaderAvatarGroup';
import { DealPlanPortalTable } from '../components/DealPlanPortalTable';
import { MilestoneDetailsPanel } from '../components/MilestoneDetailPanel';
import { useWidth } from 'deal-plans-lib/hooks/useWidth';
import { useSelectMilestone } from '../hooks/useSelectMilestone';
import { usePortalTracking } from '../hooks/usePortalTracking';
import { useTrackPlanVisited } from '../hooks/useTrackPlanVisited';
import { trackPortalInteraction } from '../utils/usageTracker';
import { buildAvatarEntities } from '../utils/avatarUtils';
import { CompanyCandidateTypename } from 'deal-plans-lib/components/milestone/types';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Home() {
  var _currentSpaceNavItem$, _currentSpaceNavItem$2;
  const {
    dealPlanId
  } = useParams();
  const {
    user
  } = usePortalsAuth();
  const isHubSpotUser = !!(user !== null && user !== void 0 && user.userId);
  const {
    navItems
  } = usePortalNavigation();
  const currentSpaceNavItem = navItems.find(item => item.isCurrent);
  const firstDealPlanPath = (_currentSpaceNavItem$ = currentSpaceNavItem === null || currentSpaceNavItem === void 0 || (_currentSpaceNavItem$2 = currentSpaceNavItem.children[0]) === null || _currentSpaceNavItem$2 === void 0 ? void 0 : _currentSpaceNavItem$2.href) !== null && _currentSpaceNavItem$ !== void 0 ? _currentSpaceNavItem$ : currentSpaceNavItem === null || currentSpaceNavItem === void 0 ? void 0 : currentSpaceNavItem.href;
  const {
    loading: dealPlanLoading,
    error,
    data
  } = useGetDealPlanQuery({
    variables: {
      objectId: Number(dealPlanId)
    }
  });
  const {
    data: ownerCandidatesData
  } = useGetOwnerCandidatesQuery({
    variables: {
      objectId: Number(dealPlanId)
    }
  });
  const {
    data: contactsData,
    loading: contactsLoading
  } = useGetCrmObjectContactsQuery({
    variables: {
      objectId: Number(dealPlanId)
    }
  });
  const dealPlanContacts = useMemo(() => {
    var _contactsData$contact;
    return (_contactsData$contact = contactsData === null || contactsData === void 0 ? void 0 : contactsData.contacts) !== null && _contactsData$contact !== void 0 ? _contactsData$contact : [];
  }, [contactsData === null || contactsData === void 0 ? void 0 : contactsData.contacts]);
  const avatarEntities = useMemo(() => {
    var _ownerCandidatesData$;
    return buildAvatarEntities((_ownerCandidatesData$ = ownerCandidatesData === null || ownerCandidatesData === void 0 ? void 0 : ownerCandidatesData.candidates.filter(c => {
      return c.__typename === CompanyCandidateTypename;
    })) !== null && _ownerCandidatesData$ !== void 0 ? _ownerCandidatesData$ : [], dealPlanContacts);
  }, [ownerCandidatesData === null || ownerCandidatesData === void 0 ? void 0 : ownerCandidatesData.candidates, dealPlanContacts]);
  const {
    data: avatarsData,
    loading: avatarsLoading
  } = useGetAvatarsBulkQuery({
    variables: {
      objectId: Number(dealPlanId),
      entities: avatarEntities
    },
    skip: avatarEntities.length === 0
  });
  return /*#__PURE__*/_jsxs(AvatarsProvider, {
    data: avatarsData !== null && avatarsData !== void 0 ? avatarsData : null,
    children: [/*#__PURE__*/_jsx(PortalsAppLayout, {
      logoAccessory: /*#__PURE__*/_jsx(BuyerLogoSection, {
        isAuthenticated: !!user,
        dealPlanId: Number(dealPlanId),
        buyerLogoUrl: data === null || data === void 0 ? void 0 : data.buyerLogoUrl
      }),
      children: /*#__PURE__*/_jsx(PortalsAuthGuard, {
        children: /*#__PURE__*/_jsx(HomeContent, {
          dealPlanId: dealPlanId,
          isHubSpotUser: isHubSpotUser,
          firstDealPlanPath: firstDealPlanPath,
          dealPlanLoading: dealPlanLoading,
          dealPlanError: error,
          dealPlanData: data,
          ownerCandidatesData: ownerCandidatesData,
          dealPlanContacts: dealPlanContacts,
          avatarsLoading: avatarsLoading,
          contactsLoading: contactsLoading
        })
      })
    }), /*#__PURE__*/_jsx(FloatingAlertList, {})]
  });
}
function HomeContent({
  dealPlanId,
  isHubSpotUser,
  firstDealPlanPath,
  dealPlanLoading,
  dealPlanError,
  dealPlanData,
  ownerCandidatesData,
  dealPlanContacts,
  avatarsLoading,
  contactsLoading
}) {
  var _dealPlanData$milesto, _dealPlanData$contact, _dealPlanData$contact2, _dealPlanData$name, _ownerCandidatesData$2, _dealPlanData$descrip;
  const milestones = (_dealPlanData$milesto = dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.milestones) !== null && _dealPlanData$milesto !== void 0 ? _dealPlanData$milesto : [];
  const {
    selectedMilestoneId,
    selectedMilestone,
    onCloseMilestoneSidebar
  } = useSelectMilestone(milestones);
  const {
    userRole,
    trackShareClicked
  } = usePortalTracking(dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.dealId);
  const isShared = ((_dealPlanData$contact = dealPlanData === null || dealPlanData === void 0 || (_dealPlanData$contact2 = dealPlanData.contacts) === null || _dealPlanData$contact2 === void 0 ? void 0 : _dealPlanData$contact2.length) !== null && _dealPlanData$contact !== void 0 ? _dealPlanData$contact : 0) > 0;
  const {
    updateDescription
  } = useDealPlanActions(Number(dealPlanId), (_dealPlanData$name = dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.name) !== null && _dealPlanData$name !== void 0 ? _dealPlanData$name : '', dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.dealId);
  const [headerRef, headerWidth] = useWidth();
  useTrackPlanVisited(Number(dealPlanId), {
    skip: isHubSpotUser
  });
  const isDealPlanLoaded = dealPlanData !== undefined;
  useEffect(() => {
    if (!isDealPlanLoaded) return;
    trackPortalInteraction('viewed', {
      userRole,
      dealId: dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.dealId
    });
  }, [userRole, isDealPlanLoaded, dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.dealId]);
  useEffect(() => {
    if (dealPlanData !== null && dealPlanData !== void 0 && dealPlanData.name) {
      document.title = dealPlanData.name;
    }
  }, [dealPlanData === null || dealPlanData === void 0 ? void 0 : dealPlanData.name]);
  const isDataLoading = dealPlanLoading || avatarsLoading || contactsLoading;
  if (isDataLoading) {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      minBlockSize: "100%",
      padding: "800",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        "data-test-id": "deal-plan-loading"
      })
    });
  }
  if (dealPlanError || !dealPlanData) {
    return /*#__PURE__*/_jsx(PageNotFound, {
      homePath: firstDealPlanPath
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "INDEX_LOAD",
    children: /*#__PURE__*/_jsxs(Flex, {
      flex: "1",
      minBlockSize: "0",
      blockSize: "100%",
      alignItems: "stretch",
      children: [/*#__PURE__*/_jsxs(View, {
        flex: "1",
        minInlineSize: "0",
        overflow: "auto",
        paddingInline: "600",
        paddingBlock: '400',
        paddingBlockStart: "900",
        children: [/*#__PURE__*/_jsx("div", {
          ref: headerRef,
          children: /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "end",
            alignItems: "stretch",
            gap: "200",
            marginBlockEnd: "400",
            children: [/*#__PURE__*/_jsx(HeaderAvatarGroup, {
              ownerCandidates: (_ownerCandidatesData$2 = ownerCandidatesData === null || ownerCandidatesData === void 0 ? void 0 : ownerCandidatesData.candidates) !== null && _ownerCandidatesData$2 !== void 0 ? _ownerCandidatesData$2 : [],
              dealPlanContacts: dealPlanContacts,
              headerWidth: headerWidth
            }), /*#__PURE__*/_jsx(InviteButton, {
              objectId: Number(dealPlanId),
              onInviteSuccess: trackShareClicked
            }), isHubSpotUser && dealPlanData.dealId != null && /*#__PURE__*/_jsx(View, {
              flexShrink: 0,
              children: /*#__PURE__*/_jsx(DealPlanHeaderMenu, {
                dealPlanId: Number(dealPlanId),
                dealId: dealPlanData.dealId,
                isShared: isShared
              })
            })]
          })
        }), /*#__PURE__*/_jsxs(View, {
          maxInlineSize: "900px",
          marginInlineStart: "auto",
          marginInlineEnd: "auto",
          children: [/*#__PURE__*/_jsx(DealPlanName, {
            name: dealPlanData.name,
            dealPlanId: dealPlanData.dealPlanId,
            isHubSpotUser: isHubSpotUser
          }), isHubSpotUser ? /*#__PURE__*/_jsx(DealPlanDescriptionHoverButton, {
            description: (_dealPlanData$descrip = dealPlanData.description) !== null && _dealPlanData$descrip !== void 0 ? _dealPlanData$descrip : '',
            onSave: updateDescription,
            dealId: dealPlanData.dealId
          }) : dealPlanData.description ? /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "600",
            children: /*#__PURE__*/_jsx(SanitizedText, {
              body: dealPlanData.description
            })
          }) : null, /*#__PURE__*/_jsx(DealPlanPortalTable, {
            dealPlanId: dealPlanData.dealPlanId,
            milestones: milestones,
            selectedMilestoneId: selectedMilestoneId !== null && selectedMilestoneId !== void 0 ? selectedMilestoneId : undefined,
            dealId: dealPlanData.dealId
          })]
        })]
      }), selectedMilestone && /*#__PURE__*/_jsx(MilestoneDetailsPanel, {
        milestone: selectedMilestone,
        onClose: onCloseMilestoneSidebar
      })]
    })
  });
}